import { Box, Typography, styled } from '@mui/material';
import classNames from 'classnames';

import { ORGAN_COLORS, ORGAN_SHAPES } from '#shared';

interface LegendProps {
  embedded?: boolean;
}

const PREFIX = 'TSMED-Legend';
const classes = {
  root: `${PREFIX}-root`,
  legendItem: `${PREFIX}-legendItem`,
  shapeContainer: `${PREFIX}-shapeContainer`,
  shape: `${PREFIX}-shape`,
  circle: `${PREFIX}-circle`,
  rhombus: `${PREFIX}-rhombus`,
  caption: `${PREFIX}-caption`,
};

const Root = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));

const LegendItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'embedded',
})<{ embedded: boolean }>(({ embedded }) => ({
  display: embedded ? 'flex' : 'inline-block',
  alignItems: 'center',
  color: '#FFF',
  textAlign: 'center',
}));

const ShapeContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'embedded',
})<{ embedded: boolean }>(({ theme: { spacing }, embedded }) => ({
  display: 'inline-block',
  marginRight: embedded ? spacing(1) : 0,

  [`& .${classes.shape}`]: {
    height: 6,
    width: 6,
  },

  [`& .${classes.circle}`]: {
    borderRadius: '50%',
  },

  [`& .${classes.rhombus}`]: {
    rotate: '45deg',
  },
}));

const Caption = styled(Typography)(({ theme: { utils } }) => ({
  opacity: '30%',
  fontSize: utils.clamp(10, 16),
}));

/**
 * Legend component that displays organ shapes and colors
 */
export const Legend = ({ embedded = false }: LegendProps) => {
  const legendItems = [
    { color: ORGAN_COLORS.Heart, shape: ORGAN_SHAPES.Heart, caption: 'Heart' },
    { color: ORGAN_COLORS.Lungs, shape: ORGAN_SHAPES.Lungs, caption: 'Lungs' },
    { color: ORGAN_COLORS.Liver, shape: ORGAN_SHAPES.Liver, caption: 'Liver' },
  ];

  return (
    <Root className={classes.root}>
      {legendItems.map(curLegendItem => (
        <LegendItem
          className={classes.legendItem}
          key={curLegendItem.caption}
          embedded={embedded}
        >
          <ShapeContainer
            embedded={embedded}
            className={classes.shapeContainer}
          >
            <Box
              className={classNames(
                classes.shape,
                classes[curLegendItem.shape],
              )}
              style={{ backgroundColor: curLegendItem.color }}
            />
          </ShapeContainer>
          <Caption className={classes.caption}>{curLegendItem.caption}</Caption>
        </LegendItem>
      ))}
    </Root>
  );
};
